import { FunctionComponent } from "preact";
import {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
} from "preact/hooks";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable"; // The default
import Logo from "../Logo";
import useDebounce from "../../hooks/useDebounce";
import isNil from "lodash/isNil";
import isNumber from "lodash/isNumber";
import useLocalStorage from "../../hooks/useLocalStorage";
import ChevronLeft from "../Icons/ChevronLeft";
import ChevronRight from "../Icons/ChevronRight";

interface Props {
  onClick: Function;
  sidebarVisible: boolean;
}

const buttonHeight = 40;
// const defaultInitialY = -buttonHeight;
const storageButtonPositionKey = "plugilo_button_position";

const SidebarButton: FunctionComponent<Props> = ({
  onClick,
  sidebarVisible,
}) => {
  const firstLoad = useRef<boolean>();
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [isDragging, setDragging] = useState<boolean>(false);
  const [isHovering, setHovering] = useState<boolean>(false);
  const [savedPosition, savePosition] = useLocalStorage(
    storageButtonPositionKey
  );
  const [position, setPosition] = useState({
    x: 0,
    y: 0,
  });
  const debouncedPosition = useDebounce(position, 1000);

  useLayoutEffect(() => {
    const initialSavedPosition = savedPosition as any;
    let initialY =
      Math.floor(window.innerHeight / 4) - Math.floor(buttonHeight / 2);

    if (initialSavedPosition && isNumber(initialSavedPosition.y)) {
      initialY = initialSavedPosition.y;
    }

    initialY = initialY >= 0 ? initialY : 0;
    const maxY = window.innerHeight - buttonHeight;
    const finalY = maxY < initialY ? maxY : initialY;

    setPosition({
      ...position,
      y: Math.max(finalY, 0),
    });
  }, []);

  useEffect(() => {
    const onFulLScreenChange = (): void => {
      setIsVisible(!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", onFulLScreenChange);

    return (): void => {
      document.removeEventListener("fullscreenchange", onFulLScreenChange);
    };
  }, []);

  useEffect(() => {
    const resize = (): void => {
      let maxY = window.innerHeight - buttonHeight;
      maxY = maxY >= 0 ? maxY : 0;

      if (maxY < position.y) {
        setPosition({
          ...position,
          y: maxY,
        });
      }
    };

    window.addEventListener("resize", resize);

    return (): void => {
      window.removeEventListener("resize", resize);
    };
  }, [position]);

  useEffect(() => {
    firstLoad.current = isNil(firstLoad.current);
    if (!firstLoad.current) {
      savePosition(debouncedPosition);
    }
  }, [debouncedPosition]);

  const onDrag = (_e: DraggableEvent, newPosition: DraggableData): void => {
    const { y } = newPosition;
    setPosition({
      x: position.x,
      y,
    });
    setDragging(true);
  };

  const onStop = (): void => {
    setDragging(false);
    if (!isDragging && onClick) {
      onClick();
    }
  };

  const buttonIcon = useMemo(() => {
    if (sidebarVisible) return <ChevronLeft />;
    if (isHovering || isDragging) return <ChevronRight />;

    return <Logo />;
  }, [isDragging, isHovering, sidebarVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <Draggable
      bounds="parent"
      position={position}
      axis="y"
      onDrag={onDrag}
      onStop={onStop}
      defaultClassName="plugilo-sidebar-button-draggable"
      defaultClassNameDragging="plugilo-sidebar-button-draggable-dragging"
      defaultClassNameDragged="plugilo-sidebar-button-draggable-dragged"
    >
      <div
        className="sidebar-button-frame-wrapper"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <div className="sidebar-draggable-zone" />
        <div
          style={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            borderRadius: "0 20px 20px 0",
            color: "#343a40",
            background: "#fff",
            border: "1px solid #dee2e6",
            borderLeft: "none",
          }}
        >
          {buttonIcon}
        </div>
      </div>
    </Draggable>
  );
};

export default SidebarButton;
